import React, { useState } from "react";
import { GrFormClose } from "react-icons/gr";
import { AiOutlineCloudUpload } from "react-icons/ai";
import ProgressBarLine from "../Progressbar/progressbar";
import Button from "../Button/Button";
import { toast } from "react-hot-toast";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { PDFDocument } from "pdf-lib";
import UploadFileIcon from "../icons/uploadFile";

function UploadFileModel({ setstate, PDFUI, type, initial, setInitial }) {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState("open");
  const [upload, setUpload] = useState(false);
  const [progress, setProgress] = useState(0);
  const [disableBtn, setDisableBtn] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { t, i18n } = useTranslation();

  const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB ", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  const validatePDF = async (file) => {
    try {
      // 1. File type validation
      if (!file.type.includes("pdf")) {
        return { isValid: false, error: "Please upload a valid PDF file" };
      }

      // 2. Size validation
      const maxFileSize = 200 * 1024 * 1024; // 200MB
      const minFileSize = 0;

      if (file.size > maxFileSize) {
        const maxSizeMB = maxFileSize / (1024 * 1024);
        return {
          isValid: false,
          error: `File size cannot be more than ${maxSizeMB} MB`,
        };
      }

      if (file.size < minFileSize) {
        return { isValid: false, error: "File size cannot be empty" };
      }

      // 3. PDF specific validations
      try {
        const arrayBuffer = await file.arrayBuffer();

        // Load PDF with ignoreEncryption option
        const pdfDoc = await PDFDocument.load(arrayBuffer, {
          ignoreEncryption: true,
          updateMetadata: false,
        });

        // Check if PDF is encrypted
        if (pdfDoc.isEncrypted) {
          return {
            isValid: false,
            error: "Password protected PDFs are not supported",
          };
        }

        // Check page count
        const pageCount = pdfDoc.getPageCount();
        if (pageCount === 0) {
          return { isValid: false, error: "The PDF file is empty" };
        }

        const maxFileLength = 500; // Maximum pages allowed
        if (pageCount > maxFileLength) {
          return {
            isValid: false,
            error: `${file.name} contains more than ${maxFileLength} pages and cannot be uploaded.`,
          };
        }

        return { isValid: true };
      } catch (error) {
        console.error("PDF validation error:", error);

        // Handle specific PDF errors
        if (error instanceof Error) {
          if (error.message.includes("encrypted")) {
            return {
              isValid: false,
              error: "Password protected PDFs are not supported",
            };
          } else if (error.message.includes("Failed to parse PDF document")) {
            return {
              isValid: false,
              error: "The PDF file is corrupted or invalid",
            };
          }
        }

        return {
          isValid: false,
          error: "The selected PDF file is corrupted. Please select a valid PDF file.",
        };
      }
    } catch (error) {
      console.error("Validation error:", error);
      return { isValid: false, error: "Error validating PDF file" };
    }
  };

  const handleUpload = async () => {
    if (file) {
      const isValidPDF = await validatePDF(file);

      if (!isValidPDF.isValid) {
        toast.error(isValidPDF.error);
        return;
      }
      setUpload(true);
      setProgress(100);

      if (type === "new") {
        Cookies.set("file_name", file?.name, {
          domain: process.env.NEXT_PUBLIC_DOMAIN,
          path: "/",
        });

        Cookies.remove("file_Id", {
          domain: process.env.REACT_APP_PUBLIC_DOMAIN,
          path: "/",
        });

        Cookies.remove("file_url", {
          domain: process.env.REACT_APP_PUBLIC_DOMAIN,
          path: "/",
        });

        setInitial(false);

        PDFUI.openPDFByFile(file);
      } else {
        PDFUI.getPDFDocRender().then(async (document) => {
          const PDFDoc = document.getPDFDoc();
          // const pageCount = PDFDoc.getPageCount();
          // console.log(pageCount); // I did get the page count here
          // console.log(file); // and file object here
          const docFile = await PDFUI.loadPDFDocByFile(file);
          // console.log("docFile", docFile);
          const mergeFile = await PDFDoc.mergePDFDoc({
            doc: docFile,
          });
        });
      }

      setstate();
    } else {
      toast.error(t("Kindly select a file"));
    }
  };

  const handleNewFile = async () => {
    Cookies.remove("file_name");
    Cookies.remove("file_Id");

    await PDFUI.createNewDoc(
      "GoPDF.pdf",
      "GoPDF",
      { height: 842, width: 595 },
      { isRenderOnDocLoaded: true },
      PDFUI.pdfEngine
    );

    setstate();
  };
  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragging(false);
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      const validationResult = await validatePDF(droppedFile);
      if (!validationResult.isValid) {
        toast.error(t(validationResult.error));
        return;
      }

      try {
        setFile(droppedFile);
        setUpload(true);
        setProgress(100);

        if (type === "new") {
          Cookies.set("file_name", droppedFile?.name, {
            domain: process.env.NEXT_PUBLIC_DOMAIN,
            path: "/",
          });

          Cookies.remove("file_Id", {
            domain: process.env.REACT_APP_PUBLIC_DOMAIN,
            path: "/",
          });

          Cookies.remove("file_url", {
            domain: process.env.REACT_APP_PUBLIC_DOMAIN,
            path: "/",
          });

          setInitial(false);
          PDFUI.openPDFByFile(droppedFile);
        } else {
          PDFUI.getPDFDocRender().then(async (document) => {
            const PDFDoc = document.getPDFDoc();
            const docFile = await PDFUI.loadPDFDocByFile(droppedFile);
            const mergeFile = await PDFDoc.mergePDFDoc({
              doc: docFile,
            });
          });
        }

        setstate();
      } catch (error) {
        console.error("Error processing file:", error);
        toast.error(t("Error processing file"));
      }
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validationResult = await validatePDF(selectedFile);
      if (!validationResult.isValid) {
        toast.error(t(validationResult.error));
        return;
      }

      try {
        setFile(selectedFile);
        setUpload(true);
        setProgress(100);

        if (type === "new") {
          Cookies.set("file_name", selectedFile?.name, {
            domain: process.env.NEXT_PUBLIC_DOMAIN,
            path: "/",
          });

          Cookies.remove("file_Id", {
            domain: process.env.REACT_APP_PUBLIC_DOMAIN,
            path: "/",
          });

          Cookies.remove("file_url", {
            domain: process.env.REACT_APP_PUBLIC_DOMAIN,
            path: "/",
          });

          setInitial(false);
          PDFUI.openPDFByFile(selectedFile);
        } else {
          PDFUI.getPDFDocRender().then(async (document) => {
            const PDFDoc = document.getPDFDoc();
            const docFile = await PDFUI.loadPDFDocByFile(selectedFile);
            const mergeFile = await PDFDoc.mergePDFDoc({
              doc: docFile,
            });
          });
        }

        setstate();
      } catch (error) {
        console.error("Error processing file:", error);
        toast.error(t("Error processing file"));
      }
    }
  };

  return (
    <div className="modal max-w-xl !w-full m-auto sm:!w-[40rem]">
      <h3 className={`modal-heading mb-6 px-5 w-full`}>
        {t("Upload and attach files")}
      </h3>

      {!file && fileType === "open" ? (
        <div className="flex flex-wrap w-full justify-center items-center md:p-4 rounded-2xl bg-primary-400 drop-shadow-7xl mb-6">
          <label
            className="cursor-pointer flex justify-center items-center border border-primary border-dashed px-6 py-10 rounded-xl w-full"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              accept=".pdf"
              type="file"
              className="w-0 hidden absolute"
              value={[]}
              onChange={handleFileChange}
            />

            <div className="flex flex-col justify-center items-center w-full text-center">
              <UploadFileIcon className="mb-4 w-32 h-40" />
              {/* <div className="h-[4.375rem] w-[4.375rem] mb-4 rounded-full bg-black flex justify-center items-center text-[2.375rem] text-white">
                <AiOutlineCloudUpload />
              </div> */}
              <h4 className="text-secondary-1000 text-2xl !font-medium mb-2">
                {t("Drag and Drop")}
              </h4>
              <p className="mb-1 text-secondary-650 text-base text-center">
                {" "}
                <span className="border-b border-secondary-650">
                  {t("Click to upload")}
                </span>{" "}
                {t("or drag and drop")}
              </p>
              <p className="mb-1 text-secondary-650 text-base text-center">
                {" "}
                <span className="border-b border-secondary-650">
                  {t("File should be less than 200 MB")}
                </span>{" "}
                {t("and allowed page size is 500")}
              </p>
              <p className="mb-1 text-secondary-650 text-base text-center">
                {t("Only one file upload")}
              </p>
              <Button className="!w-max mt-3 pointer-events-none">
                {t("Select File")}
              </Button>
            </div>
          </label>
        </div>
      ) : file ? (
        <div className="flex flex-wrap w-full justify-center items-center md:p-4 rounded-2xl bg-primary-400 drop-shadow-7xl mb-6">
          <div className="flex justify-center items-center border border-primary border-dashed px-6 py-10 rounded-xl w-full">
            {!upload && (
              <GrFormClose
                onClick={() => {
                  setFile(null);
                }}
                className="absolute top-5 right-5 text-3xl text-[#2E2E2E] cursor-pointer"
              />
            )}
            <div className="flex flex-col justify-center items-center w-full text-center">
              <UploadFileIcon className="mb-4 w-32 h-40" />

              <p
                className="text-secondary-1000 text-2xl !font-medium mb-2"
                style={{ overflowWrap: "anywhere" }}
              >
                {
                  //@ts-ignore
                  file.name
                }
              </p>
              <p className="mb-1 text-secondary-650 text-base text-center">
                {
                  //@ts-ignore
                  formatBytes(file.size)
                }
              </p>

              {!upload ? (
                <Button
                  disabled={disableBtn}
                  className="!w-max mt-3"
                  onClick={() => {
                    handleUpload();
                  }}
                >
                  {t("Upload File")}
                </Button>
              ) : (
                <div>
                  <ProgressBarLine percent={progress} />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex !hidden flex-wrap w-full justify-center items-center md:p-4 rounded-2xl bg-primary-400 drop-shadow-7xl mb-6">
          <label className="flex justify-center items-center border border-primary border-dashed px-6 py-10 rounded-xl w-full">
            <div
              onClick={() => {
                handleNewFile();
              }}
              className="flex flex-col justify-center items-center w-full text-center"
            >
              <UploadFileIcon className="mb-4 w-32 h-40" />
              {/* <i className="icon-uplaodfile h-[4.375rem] w-[4.375rem] mb-4 rounded-full bg-black flex justify-center items-center text-[2.375rem] text-white"></i> */}
              {/* <div className="h-[4.375rem] w-[4.375rem] mb-4 rounded-full bg-black flex justify-center items-center text-[2.375rem] text-white">
                <AiOutlineCloudUpload />
              </div> */}
              <p className="mb-1 text-secondary-650 text-base text-center">
                <span className="border-b border-secondary-650">
                  {t("New Blank File")}
                </span>
              </p>
            </div>
          </label>
        </div>
      )}

      <div className="flex justify-center items-center mb-4 gap-4">
        <hr className="w-full" />
        <p className="text-center text-secondary-650 text-base">or</p>
        <hr className="w-full" />
      </div>

      <div className="w-full">
        <Button
          className="w-full"
          onClick={() => {
            handleNewFile();
          }}
        >
          {t("Create new PDF")}
        </Button>
      </div>
    </div>
  );
}

export default UploadFileModel;
